/**
 * Lets a hash route change be declined without leaving an extra history entry behind.
 *
 * A `hashchange` event does not say how the route changed: a link click or a `location.hash`
 * assignment pushes a new entry, while Back or Forward moves to an existing one. Each accepted
 * route therefore stamps its entry in `history.state` with the entry's position in the session
 * history. When a change is declined, an entry without a stamp was just pushed and is left with
 * `history.back()` (the next navigation drops it), and a stamped entry was reached with Back or
 * Forward, which is undone by moving the same distance the other way.
 */

/** Key in `history.state` that holds the position of an accepted route's entry. */
export const HISTORY_POSITION_KEY = 'wbPosition';

/**
 * @param {Pick<History, 'state'>} historyApi
 * @returns {number | null} the stamped position of the current entry, or null when it has none
 */
function stampedPosition(historyApi) {
    const position = historyApi.state?.[HISTORY_POSITION_KEY];
    return Number.isInteger(position) ? position : null;
}

/**
 * Stamps the current entry with its position, once a route change to it is accepted. An entry
 * without a stamp was just pushed from the entry of the previous accepted route (a push drops
 * every entry after the current one), so it sits one position after that entry. Only the first
 * route of the page, with no previous entry, takes its position from `history.length`: browsers
 * cap the session history (Chrome keeps 50 entries), so past the cap every push would get the
 * same length-based position and Back could no longer be undone.
 *
 * @param {number | null} previousPosition the position of the previous accepted route's entry,
 *     or null for the first route of the page
 * @param {Pick<History, 'state' | 'length' | 'replaceState'>} [historyApi]
 * @returns {number} the position of the current entry
 */
export function markAcceptedEntry(previousPosition, historyApi = history) {
    const stamped = stampedPosition(historyApi);
    if (stamped !== null) {
        return stamped;
    }
    const position = previousPosition === null ? historyApi.length - 1 : previousPosition + 1;
    historyApi.replaceState({...historyApi.state, [HISTORY_POSITION_KEY]: position}, '');
    return position;
}

/**
 * Goes back to the entry at `acceptedPosition` after a declined route change, without adding an
 * entry. The `hashchange` this causes reports the route that is already on screen.
 *
 * @param {number} acceptedPosition what `markAcceptedEntry` returned for the route on screen
 * @param {Pick<History, 'state' | 'go'>} [historyApi]
 */
export function returnToAcceptedEntry(acceptedPosition, historyApi = history) {
    const stamped = stampedPosition(historyApi);
    const delta = stamped === null ? -1 : acceptedPosition - stamped;
    if (delta !== 0) {
        historyApi.go(delta);
    }
}
